import * as React from 'react';
import { useRouter } from 'next/router';
import useOnboarding, { OnboardingGuide, OnboardingStepStatus } from '../../hooks/useOnboarding';
import { checkoutEditorUrl, resolveShopDomain } from '../../lib/shop-domain';

const StepMarker: React.FC<{ status: OnboardingStepStatus | 'info' }> = ({ status }) => {
  if (status === 'complete') {
    return (
      <span className='tb-setup-marker is-complete' aria-label='Complete'>
        <s-icon type='check' tone='success' size='small' />
      </span>
    );
  }
  if (status === 'unknown') {
    return (
      <span className='tb-setup-marker is-unknown' aria-label='Not verified'>
        <s-icon type='alert' tone='caution' size='small' />
      </span>
    );
  }
  if (status === 'info') {
    return (
      <span className='tb-setup-marker is-info' aria-label='Optional'>
        <s-icon type='info' size='small' />
      </span>
    );
  }
  return <span className='tb-setup-marker' aria-label='Incomplete' />;
};

/**
 * Illustration of the checkout editor's "Add app block" picker, mirroring what the merchant
 * will actually see there. Drawn inline rather than screenshotted so it stays crisp, ships no
 * image asset, and never leaks a dev store's app list. The wrapper div + CSS aspect-ratio give
 * it a concrete box: a bare <svg> with only a viewBox can collapse to zero in some engines
 * (Safari) when sized by flex alone.
 */
const DownloadsRecapFigure: React.FC = () => (
  <div className='tb-setup-downloads-figure'>
    <svg
      viewBox='0 0 480 190'
      role='img'
      aria-label='Checkout editor: Tuneboom Downloads app block with an Add to menu listing Thank you and Order status'
    >
      <text x='16' y='30' fontSize='15' fontWeight='700' fill='#1a1a1a'>
        Tuneboom
      </text>
      {/* App block row */}
      <rect x='16' y='46' width='280' height='52' rx='10' fill='#ffffff' stroke='#d9d9d9' />
      <rect x='30' y='58' width='28' height='28' rx='7' fill='#eef0ff' />
      <path
        d='M38 78l6-12 3 6 3-6 4 12'
        stroke='#6366f1'
        strokeWidth='2.4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <text x='70' y='77' fontSize='14' fill='#1a1a1a'>
        Tuneboom Downloads
      </text>
      <circle cx='272' cy='72' r='10' fill='none' stroke='#3b5bdb' strokeWidth='1.8' />
      <path d='M272 67v10M267 72h10' stroke='#3b5bdb' strokeWidth='1.8' strokeLinecap='round' />
      {/* "Add to" popover */}
      <rect x='300' y='52' width='164' height='118' rx='12' fill='#ffffff' stroke='#e0e0e0' />
      <text x='318' y='82' fontSize='14' fontWeight='700' fill='#1a1a1a'>
        Add to
      </text>
      <circle cx='327' cy='106' r='8' fill='none' stroke='#4a4a4a' strokeWidth='1.6' />
      <path
        d='M323.5 106l2.5 2.5 4.5-5'
        stroke='#4a4a4a'
        strokeWidth='1.6'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <text x='344' y='111' fontSize='14' fill='#1a1a1a'>
        Thank you
      </text>
      <circle cx='327' cy='140' r='8' fill='none' stroke='#4a4a4a' strokeWidth='1.6' />
      <path
        d='M327 134a6 6 0 106 6'
        stroke='#4a4a4a'
        strokeWidth='1.6'
        fill='none'
        strokeLinecap='round'
      />
      <text x='344' y='145' fontSize='14' fill='#1a1a1a'>
        Order status
      </text>
    </svg>
  </div>
);

/** `undefined` = follow the guide (next incomplete step opens); `''` = everything collapsed. */
type ExpandedId = string | undefined;

export interface SetupGuideViewProps {
  guide: OnboardingGuide;
  isValidating?: boolean;
  onRecheck: () => void;
  onNavigate: (href: string) => void;
  /** myshopify domain, used for the checkout-editor deep link. */
  shopDomain?: string;
}

/**
 * Presentational setup guide, an accordion of one-open-at-a-time steps: only the step the
 * merchant should act on next is expanded, the rest collapse to a marker + title, so the card
 * reads as a short checklist instead of a wall of instructions. Clicking any row opens it.
 *
 * Every row's state comes from the server, which re-derives it from the catalog and the
 * merchant's published theme on each request — the checklist cannot be ticked by clicking it,
 * and the "press Save" banner exists because a deep link that opens the theme editor changes
 * nothing until the merchant saves there.
 *
 * The trailing "Tuneboom Downloads" row is informational, not a step: it is placed in the
 * checkout editor, which Shopify exposes no read API for, so it could never verify and never
 * counts toward progress.
 *
 * Exported separately from the data-bound default so /preview/setup-guide can render every
 * state from fixtures.
 */
export const SetupGuideView: React.FC<SetupGuideViewProps> = ({
  guide,
  isValidating,
  onRecheck,
  onNavigate,
  shopDomain,
}) => {
  const { steps, completedCount, totalCount } = guide;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const nextStepId = steps.find((step) => step.status !== 'complete')?.id;

  const [expanded, setExpanded] = React.useState<ExpandedId>(undefined);
  const openId = expanded === undefined ? nextStepId : expanded || undefined;

  const editorUrl = shopDomain ? checkoutEditorUrl(shopDomain) : undefined;

  const row = (args: {
    id: string;
    title: string;
    marker: OnboardingStepStatus | 'info';
    children: React.ReactNode;
  }) => {
    const open = openId === args.id;
    return (
      <li key={args.id} className='tb-setup-step'>
        <button
          type='button'
          className='tb-setup-step-header'
          aria-expanded={open}
          onClick={() => setExpanded(open ? '' : args.id)}
        >
          <StepMarker status={args.marker} />
          <s-text type={args.marker === 'complete' ? 'generic' : 'strong'}>{args.title}</s-text>
          <span className='tb-setup-step-chevron' aria-hidden='true'>
            <s-icon type={open ? 'chevron-up' : 'chevron-down'} size='small' />
          </span>
        </button>
        {open && <div className='tb-setup-step-body'>{args.children}</div>}
      </li>
    );
  };

  return (
    <s-section heading='Setup guide'>
      <div className='tb-setup-body'>
        <s-paragraph color='subdued'>
          Finish these steps to get your music playing on your storefront. Your dashboard opens up
          once they are done.
        </s-paragraph>

        <div className='tb-setup-progress'>
          <span className='tb-setup-progress-label'>
            {completedCount} of {totalCount} tasks complete
          </span>
          <div
            className='tb-setup-progress-track'
            role='progressbar'
            aria-label='Setup progress'
            aria-valuenow={completedCount}
            aria-valuemin={0}
            aria-valuemax={totalCount}
          >
            <div className='tb-setup-progress-fill' style={{ width: `${pct}%` }} />
          </div>
        </div>

        <s-banner tone='info'>
          The theme editor does not save on its own. After a link opens it, press <b>Save</b> in the
          top-right corner. Nothing counts until you do.
        </s-banner>

        <ul className='tb-setup-steps'>
          {steps.map((step) => {
            const action = step.status === 'complete' ? undefined : step.action;
            return row({
              id: step.id,
              title: step.title,
              marker: step.status,
              children: (
                <>
                  <s-text color='subdued'>{step.description}</s-text>
                  {step.warning && (
                    <s-banner tone={step.status === 'unknown' ? 'caution' : 'warning'}>
                      {step.warning}
                    </s-banner>
                  )}
                  {action && (
                    <div className='tb-setup-step-action'>
                      {action.external ? (
                        <s-button variant='primary' href={action.href} target='_blank'>
                          {action.label}
                        </s-button>
                      ) : (
                        <s-button variant='primary' onClick={() => onNavigate(action.href)}>
                          {action.label}
                        </s-button>
                      )}
                    </div>
                  )}
                </>
              ),
            });
          })}

          {row({
            id: 'downloads-recap',
            title: 'Show buyers their downloads after checkout',
            marker: 'info',
            children: (
              <div className='tb-setup-downloads'>
                <div className='tb-setup-downloads-copy'>
                  <s-text color='subdued'>
                    Adds each buyer&apos;s files and signed license to the Thank you and Order
                    status pages. In the checkout editor, add the <b>Tuneboom Downloads</b> app
                    block to <b>both pages</b>, then Save. We can&apos;t tick this one for you —
                    Shopify has no API for checkout placements.
                  </s-text>
                  <s-text color='subdued'>
                    Order status needs the new customer accounts experience (Settings &rarr;
                    Customer accounts).
                  </s-text>
                  {editorUrl && (
                    <div className='tb-setup-step-action'>
                      <s-button variant='secondary' href={editorUrl} target='_blank'>
                        Open checkout editor
                      </s-button>
                    </div>
                  )}
                </div>
                <DownloadsRecapFigure />
              </div>
            ),
          })}
        </ul>

        <div className='tb-setup-foot'>
          <s-button variant='tertiary' icon='refresh' loading={isValidating} onClick={onRecheck}>
            Recheck
          </s-button>
          <s-text color='subdued'>
            Saved something in the theme editor? Come back and press Recheck. We read your saved
            theme, we never edit it.
          </s-text>
        </div>
      </div>
    </s-section>
  );
};

const SetupGuide: React.FC = () => {
  const router = useRouter();
  const { guide, isValidating, mutate } = useOnboarding();

  if (!guide || guide.complete) return null;

  return (
    <SetupGuideView
      guide={guide}
      isValidating={isValidating}
      onRecheck={() => mutate()}
      onNavigate={(href) => router.push(href)}
      shopDomain={resolveShopDomain()}
    />
  );
};

export default SetupGuide;
